import React from 'react';
import { AntdTable } from '../../components/AntdTable';
import { ColumnsType } from 'antd/es/table';
import { useSelector } from 'react-redux';
import {
    selectUserAssetsData,
    selectUserAssetsDataLoading,
} from '../../modules/user/assets/selector';
import moment from 'moment';
import {
    selectStockOrderHistoryData,
    selectStockOrderHistoryLoading,
} from '../../modules/user/stock-order-history/selector';
import { filterOrderHistoryByStatus } from '../../helpers';
import { OrderHistoryData } from '../../modules/user/stock-order-history';

export interface DataUserAssetsType {
    id: string;
    key: string;
    ticker: string;
    amount: number;
    updateAt: string;

}

interface InventoryProps {
    searchVal?: string;
    pnlExpectedMap?: Record<string, number>;

}
export const TableInventoryContainer: React.FC<InventoryProps> = (props: any) => {
    const { searchVal, pnlExpectedMap } = props;
    const data = useSelector(selectUserAssetsData).portfolios as unknown as DataUserAssetsType[];
    const [filteredData, setFilteredData] = React.useState<DataUserAssetsType[]>(data);


    const isLoadingAssets = useSelector(selectUserAssetsDataLoading);
    const isLoadingOrderHistory = useSelector(selectStockOrderHistoryLoading);
    const isLoading = isLoadingAssets && isLoadingOrderHistory;

    const orderHistory = useSelector(selectStockOrderHistoryData);
    const orderHistoryPending = filterOrderHistoryByStatus(orderHistory, ['pending']);
    const orderHistoryPartially = filterOrderHistoryByStatus(orderHistory, ['partially_filled']);

    const calculateTotalUnavailableAmount = (ticker: string) => {
        const sumTotalAmount = (orders: OrderHistoryData[]) =>
            orders
                .filter((item) => item.ticker === ticker && item.side === 'sell')
                .reduce((total, item) => total + Number(item.totalAmount), 0);

        const sumUnfilledAmount = (orders: OrderHistoryData[]) =>
            orders
                .filter((item) => item.ticker === ticker && item.side === 'sell')
                .reduce((total, item) => total + (Number(item.totalAmount) - Number(item.filledAmount)), 0);

        const amountPending = sumTotalAmount(orderHistoryPending);
        const amountPartially = sumUnfilledAmount(orderHistoryPartially);

        return amountPending + amountPartially;
    };

    React.useEffect(() => {
        if (!searchVal) {
            setFilteredData(data);
        } else {
            setFilteredData(data.filter((item: any) => item.ticker.toLowerCase().includes(searchVal.toLowerCase())));
        }
    }, [searchVal, data]);

    const columns: ColumnsType<any> = [
        {
            title: 'Ticker',
            dataIndex: 'ticker',
            key: 'ticker',
            sorter: (a, b) => a.ticker.localeCompare(b.ticker),
            render: (text) => <span>{text}</span>,
        },
        {
            title: 'Available Volume',
            dataIndex: 'amount',
            sorter: (a, b) => a.amount - b.amount,
            key: 'amount',
            render: (val) => Number(val).toLocaleString(),
        },
        {
            title: 'Locked Volume',
            dataIndex: 'locked',
            sorter: (a, b) => a.locked - b.locked,
            key: 'locked',
            render: (val) => Number(val).toLocaleString(),
        },
        {
            title: 'Total Volume',
            dataIndex: 'amount',
            sorter: (a, b) => a.amount - b.amount,
            key: 'amount',
            render: (val, item) => (Number(val) + calculateTotalUnavailableAmount(item.ticker) + Number(item.locked)).toLocaleString(),
        },
        {
            title: 'Last Update',
            dataIndex: 'updatedAt',
            sorter: (a, b) => (moment(a.updatedAt).isBefore(b.updatedAt) ? -1 : 1),
            key: 'updatedAt',
            render: (val) => moment(val).format('YYYY-MM-DD HH:mm:ss'),
        },
       
        {
            title: 'PnL Expected',
            key: 'value',
            render: (_: any, item: DataUserAssetsType) => {
                const valueData = pnlExpectedMap[item.ticker];
                if (valueData === undefined) return '-';
                const color = valueData > 0 ? 'green' : valueData < 0 ? 'red' : 'inherit';
                const sign = valueData > 0 ? '+' : valueData < 0 ? '-' : '';
                return (
                    <span style={{ color, fontWeight: 600 }}>
                        {sign}{Math.abs(valueData).toFixed(2) + '%'}
                    </span>
                );
            },
        }
    ];

    return (
        <AntdTable
            loading={isLoading}
            className="custom-table"
            columns={columns}
            dataSource={filteredData}
            pagination={{ pageSize: 15 }}
        />
    );
};
